/**
 * WalterGO Product Gallery
 * Thumbnail navigation (image + video), expand/collapse, optional
 * auto-advance from first image to first video.
 */

class WaltergoProductGallery {
  constructor(container) {
    if (container.__waltergoGalleryInit) return;
    container.__waltergoGalleryInit = true;

    this.container = container;
    this.activeMedia = container.querySelector('[data-gallery-active-media]');
    this.imageContainer = container.querySelector('[data-gallery-image-container]');
    this.thumbnails = container.querySelectorAll('[data-gallery-thumb]');
    this.expandText = container.querySelector('[data-expand-text]');
    this.isExpanded = false;

    this._autoAdvanceTimer = null;
    this._autoAdvanceCancelled = false;

    this.bindEvents();
    this.setupAutoAdvance();
  }

  bindEvents() {
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        this.cancelAutoAdvance();
        this.setActiveMedia(index);
      });
    });

    if (this.imageContainer) {
      this.imageContainer.addEventListener('click', (e) => this.handleContainerClick(e));
    }

    this.container.addEventListener('keydown', (e) => this.handleKeyboard(e));
  }

  cancelAutoAdvance() {
    if (this._autoAdvanceCancelled) return;
    this._autoAdvanceCancelled = true;
    if (this._autoAdvanceTimer) {
      clearTimeout(this._autoAdvanceTimer);
      this._autoAdvanceTimer = null;
    }
  }

  handleContainerClick(event) {
    if (event.target.closest('video')) return;
    this.cancelAutoAdvance();
    this.toggleExpand();
  }

  setActiveMedia(index) {
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
    });

    const targetThumb = this.thumbnails[index];
    if (!targetThumb) return;

    if (targetThumb.dataset.mediaType === 'video') {
      this.swapToVideo(targetThumb);
    } else {
      this.swapToImage(targetThumb);
    }

    this.isExpanded = false;
    this.imageContainer?.classList.remove('is-expanded');
    this.updateExpandText();
  }

  swapToVideo(thumb) {
    const src = thumb.dataset.videoSrc;
    if (!src) return;

    if (this.activeMedia && this.activeMedia.tagName === 'VIDEO' && this.activeMedia.dataset.currentSrc === src) {
      this.activeMedia.play().catch(() => {});
      return;
    }

    const poster = thumb.dataset.videoPoster;
    const video = document.createElement('video');
    video.className = 'waltergo-gallery__main-image waltergo-gallery__main-video';
    video.setAttribute('data-gallery-active-media', '');
    video.dataset.currentSrc = src;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;
    video.controls = true;
    video.preload = 'metadata';
    if (poster) video.poster = poster;

    const source = document.createElement('source');
    source.src = src;
    source.type = 'video/mp4';
    video.appendChild(source);

    if (this.activeMedia && this.activeMedia.isConnected) {
      this.activeMedia.replaceWith(video);
    } else if (this.imageContainer) {
      this.imageContainer.prepend(video);
    }
    this.activeMedia = video;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  swapToImage(thumb) {
    const fullSrc = thumb.dataset.fullSrc;
    const fullSrcset = thumb.dataset.fullSrcset;
    if (!fullSrc) return;

    if (this.activeMedia && this.activeMedia.tagName === 'IMG') {
      this.activeMedia.src = fullSrc;
      if (fullSrcset) this.activeMedia.srcset = fullSrcset;
      return;
    }

    const img = document.createElement('img');
    img.className = 'waltergo-gallery__main-image';
    img.setAttribute('data-gallery-active-media', '');
    img.loading = 'eager';
    img.sizes = '(min-width: 1024px) 50vw, 100vw';
    img.src = fullSrc;
    if (fullSrcset) img.srcset = fullSrcset;

    if (this.activeMedia && this.activeMedia.isConnected) {
      this.activeMedia.replaceWith(img);
    } else if (this.imageContainer) {
      this.imageContainer.prepend(img);
    }
    this.activeMedia = img;
  }

  setupAutoAdvance() {
    const delayAttr = this.container.dataset.autoAdvanceToVideo;
    if (!delayAttr) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const videoThumb = [...this.thumbnails].find((t) => t.dataset.mediaType === 'video');
    if (!videoThumb || !videoThumb.dataset.videoSrc) return;

    const activeThumb = this.container.querySelector('[data-gallery-thumb].is-active');
    if (activeThumb && activeThumb.dataset.mediaType === 'video') return;

    const delayMs = Math.max(1, parseInt(delayAttr, 10) || 3) * 1000;

    this._autoAdvanceTimer = window.setTimeout(() => {
      this._autoAdvanceTimer = null;
      if (this._autoAdvanceCancelled) return;
      this._autoAdvanceCancelled = true;
      const targetIndex = parseInt(videoThumb.dataset.galleryThumb, 10);
      if (!Number.isNaN(targetIndex)) this.setActiveMedia(targetIndex);
    }, delayMs);
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    this.imageContainer?.classList.toggle('is-expanded', this.isExpanded);
    this.updateExpandText();
  }

  updateExpandText() {
    if (this.expandText) {
      this.expandText.textContent = this.isExpanded ? 'Verkleinern' : 'Vergrößern';
    }
  }

  handleKeyboard(e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    this.cancelAutoAdvance();

    const currentIndex = [...this.thumbnails].findIndex((t) => t.classList.contains('is-active'));
    let newIndex;
    if (e.key === 'ArrowLeft') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : this.thumbnails.length - 1;
    } else {
      newIndex = currentIndex < this.thumbnails.length - 1 ? currentIndex + 1 : 0;
    }

    this.setActiveMedia(newIndex);
    this.thumbnails[newIndex]?.focus();
  }
}

function initWaltergoGalleries(root) {
  (root || document).querySelectorAll('[data-gallery]').forEach((gallery) => {
    new WaltergoProductGallery(gallery);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initWaltergoGalleries());
} else {
  initWaltergoGalleries();
}

document.addEventListener('shopify:section:load', (e) => initWaltergoGalleries(e.target));
